/**
 * DonorPeoplePicker — shared donor/person search used by cohort building, event
 * invitations, and pipeline binding.
 *
 * Searches CONTACTS AND MEMBERS. The old cohort picker called
 * `/api/members/?record_type=contact&page_size=200` and filtered client-side:
 * `record_type=contact` is a hard filter on that endpoint, so program members who
 * donate were invisible — the reason members could not be added to a donor cohort
 * — and the 200-row cap meant search silently missed people in larger orgs.
 * Search now runs server-side with the record type as an explicit, optional
 * narrowing rather than a silent default.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, Search, UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PersonResult, searchPeople } from '@/services/api/donorProfiles';

type RecordFilter = 'all' | 'contact' | 'member';

const FILTERS: { id: RecordFilter; label: string }[] = [
  { id: 'all', label: 'Everyone' },
  { id: 'contact', label: 'Contacts' },
  { id: 'member', label: 'Members' },
];

function money(n?: number | null): string {
  return `$${Math.round(n || 0).toLocaleString('en-US')}`;
}

export interface DonorPeoplePickerProps {
  /** Called with every selected person when the user confirms. */
  onAdd: (people: PersonResult[]) => Promise<void> | void;
  /** Hide people already in this cohort. */
  excludeCohortId?: number;
  multiple?: boolean;
  addLabel?: string;
  autoFocus?: boolean;
}

export const DonorPeoplePicker: React.FC<DonorPeoplePickerProps> = ({
  onAdd,
  excludeCohortId,
  multiple = true,
  addLabel = 'Add selected',
  autoFocus = false,
}) => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<RecordFilter>('all');
  const [results, setResults] = useState<PersonResult[] | null>(null);
  const [selected, setSelected] = useState<Map<number, PersonResult>>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  const run = useCallback(
    async (term: string, rt: RecordFilter) => {
      const seq = ++reqRef.current;
      setLoading(true);
      setError(null);
      try {
        const rows = await searchPeople({
          search: term.trim() || undefined,
          record_type: rt === 'all' ? undefined : rt,
          exclude_cohort_id: excludeCohortId,
          limit: 50,
        });
        // Ignore a slower earlier response landing after a newer one.
        if (seq === reqRef.current) setResults(rows);
      } catch (e: any) {
        if (seq === reqRef.current) {
          setResults([]);
          setError(e?.message || 'Search failed.');
        }
      } finally {
        if (seq === reqRef.current) setLoading(false);
      }
    },
    [excludeCohortId],
  );

  // Debounced search-as-you-type, plus an initial listing so the panel is never
  // an empty box with no way to discover anyone.
  useEffect(() => {
    const t = window.setTimeout(() => run(q, filter), q ? 250 : 0);
    return () => window.clearTimeout(t);
  }, [q, filter, run]);

  const toggle = (p: PersonResult) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(p.id)) next.delete(p.id);
      else {
        if (!multiple) next.clear();
        next.set(p.id, p);
      }
      return next;
    });
  };

  const confirm = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      await onAdd(Array.from(selected.values()));
      setSelected(new Map());
      await run(q, filter);
    } catch (e: any) {
      setError(e?.message || 'Could not add the selected people.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[14rem] flex-1">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            autoFocus={autoFocus}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full rounded border border-gray-300 py-2 pl-8 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <div className="flex rounded border border-gray-300 p-0.5 dark:border-gray-600">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`rounded px-2.5 py-1 text-xs font-medium ${
                filter === f.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="text-xs text-red-600">{error}</p> : null}

      <div className="max-h-72 divide-y overflow-y-auto rounded border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {loading && results === null ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : results && results.length > 0 ? (
          results.map((p) => {
            const isSel = selected.has(p.id);
            return (
              <button
                key={p.id}
                onClick={() => toggle(p)}
                className={`flex w-full items-center justify-between px-3 py-2 text-left ${
                  isSel ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                      {p.record_type}
                    </span>
                    {p.is_donor ? (
                      <span className="rounded bg-green-50 px-1.5 py-0.5 text-[10px] text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        donor
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-gray-400">
                    {p.email || p.phone || 'No contact details'}
                    {p.lifetime_giving > 0 ? ` · ${money(p.lifetime_giving)} lifetime` : ''}
                  </div>
                </div>
                <span
                  className={`ml-3 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                    isSel ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {isSel ? <Check size={13} /> : null}
                </span>
              </button>
            );
          })
        ) : (
          <div className="px-3 py-8 text-center text-sm text-gray-400">
            <Users size={22} className="mx-auto mb-2 opacity-40" />
            {q ? 'Nobody matches that search.' : 'No records available yet.'}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {selected.size > 0 ? `${selected.size} selected` : 'Select one or more people'}
        </span>
        <Button size="sm" disabled={selected.size === 0 || saving} onClick={confirm}>
          {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <UserPlus size={14} className="mr-1" />}
          {addLabel}
        </Button>
      </div>
    </div>
  );
};

export default DonorPeoplePicker;
